/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link } from 'react-router-dom';
import Pagination from "react-js-pagination";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import { formatUTCDate } from "../../utils/utils";
import UserSegmentToggle from "../common/UserSegmentToggle";
import { useUserSegment, watchFilterValue } from "../../context/UserSegmentContext";
import ExportButton from "../active-users/ExportButton";
import DateRangeBar, { fmt, presetRange } from "../active-users/DateRangeBar";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import UserDetailLink from "../common/UserDetailLink";
import WorkoutSummaryCards from "./WorkoutSummaryCards";
import WorkoutTrendChart from "./WorkoutTrendChart";
import WorkoutTopTypesPanel from "./WorkoutTopTypesPanel";
import "../active-users/active-users.css";
import "./WorkoutData.css";

const LIMIT = 20;
const STATE_KEY = "workoutSessionListState";
const SORT_FIELDS = ['record_count', 'latest_timestamp'];
const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'country', label: 'Country' },
  { key: 'mobile_app_version', label: 'App Version' },
  { key: 'subscription_status', label: 'Subscription' },
  { key: 'record_count', label: 'Record Count' },
  { key: 'latest_timestamp', label: 'Latest Timestamp UTC' },
];

const WorkoutSessionList = () => {
  const navigate = useNavigate();
  const { segment } = useUserSegment();                       // global watch/without/all (feature 023)
  const userSegment = watchFilterValue(segment) || 'all';     // 'with_watch' | 'without_watch' | 'all'

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [users, setData] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalUserBase, setTotalUserBase] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [subscribedUserCount, setSubscribedUserCount] = useState(0);
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  const [summary, setSummary] = useState({ current: {}, prior: {}, deltas: {} });
  const [trend, setTrend] = useState([]);
  const [topTypes, setTopTypes] = useState([]);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // Restore list state on return from a record drill-down.
    let restored = false;
    try {
      const saved = JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null');
      if (saved) {
        restored = true;
        setFromDate(saved.fromDate || ''); setToDate(saved.toDate || '');
        setDisplayFromDate(saved.fromDate ? new Date(saved.fromDate) : null);
        setDisplayToDate(saved.toDate ? new Date(saved.toDate) : null);
        setPreset(saved.preset || null);
        setSearchValue(saved.searchValue || ''); setSubscriptionFilter(saved.subscriptionFilter || '');
        // A sort saved on a retired field (earliest_timestamp, pre-087) is dropped, not sent.
        const sortOk = SORT_FIELDS.includes(saved.sortField);
        setSortField(sortOk ? saved.sortField : ''); setSortOrder(sortOk ? (saved.sortOrder || '') : ''); setPage(saved.page || 1);
      }
    } catch { /* ignore */ }
    if (!restored) {
      // Default to the last 30 days so the date filter is always a concrete window,
      // not a silent all-time query (matches 026/028/030).
      const { fromStr, toStr } = presetRange('last30', new Date());
      setFromDate(fromStr); setToDate(toStr);
      setDisplayFromDate(new Date(fromStr)); setDisplayToDate(new Date(toStr));
      setPreset('last30');
    }
    refreshToken();
  }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => {
    if (token) loadAll(page, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter);
  }, [token]);

  // Re-fetch when the global segment changes (skip initial mount).
  const didMountSegment = useRef(false);
  useEffect(() => {
    if (!didMountSegment.current) { didMountSegment.current = true; return; }
    if (!token) return;
    setPage(1);
    loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter);
  }, [segment]);

  useEffect(() => {
    sessionStorage.setItem(STATE_KEY, JSON.stringify({
      fromDate, toDate, preset, searchValue, subscriptionFilter, sortField, sortOrder, page,
    }));
  }, [fromDate, toDate, preset, searchValue, subscriptionFilter, sortField, sortOrder, page]);

  const qs = (p, f, t, s, sf, so, sub) =>
    `page=${p}&limit=${LIMIT}&from_date=${f}&to_date=${t}&search=${s}&sortField=${sf}&sortOrder=${so}&subscription_status=${sub}&user_segment=${userSegment}`;
  const rangeQs = (f, t, s, sub) =>
    `from_date=${f}&to_date=${t}&search=${s}&subscription_status=${sub}&user_segment=${userSegment}`;
  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const applyList = (d) => {
    setData(d.data || []);
    setTotalItem(d.totalItem || 0);
    setTotalUsers(d.totalUsers || 0);
    setTotalUserBase(d.totalUserCount || 0);
    setTotalRecords(d.totalRecords || 0);
    setSubscribedUserCount(d.subscribedUserCount || 0);
  };
  const handleErr = (err) => { setError(err?.response?.status === 401 ? 'auth' : 'load'); };

  const getList = async (p, f, t, s, sf, so, sub) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/workout-sessions?${qs(p, f, t, s, sf, so, sub)}`, auth());
      applyList(res.data);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const loadAll = async (p, f, t, s, sf, so, sub) => {
    setLoading(true); setError('');
    const range = rangeQs(f, t, s, sub);
    try {
      const [list, sum, tr, tt] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/workout-sessions?${qs(p, f, t, s, sf, so, sub)}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/workout-summary?${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/workout-trend?${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/workout-top-types?${range}`, auth()),
      ]);
      applyList(list.data);
      setSummary(sum.data.data || { current: {}, prior: {}, deltas: {} });
      setTrend(tr.data.data || []);
      setTopTypes(tt.data.data || []);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    getList(pageNumber, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter);
  };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    const { fromStr: fs, toStr: ts } = presetRange(key, new Date());
    setPreset(key); setFromDate(fromStr || fs); setToDate(toStr || ts);
    setDisplayFromDate(new Date(fromStr || fs)); setDisplayToDate(new Date(toStr || ts));
    setPage(1);
    loadAll(1, fromStr || fs, toStr || ts, searchValue, sortField, sortOrder, subscriptionFilter);
  };
  const handleSearch = () => { setPage(1); loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter); };
  const handleSubscription = (e) => {
    const v = e.target.value;
    setSubscriptionFilter(v); setPage(1);
    loadAll(1, fromDate, toDate, searchValue, sortField, sortOrder, v);
  };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null);
    setSearchValue(''); setSubscriptionFilter(''); setPreset(null); setPage(1);
    loadAll(1, '', '', '', sortField, sortOrder, '');
  };
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field); setSortOrder(order); setPage(1);
    loadAll(1, fromDate, toDate, searchValue, field, order, subscriptionFilter);
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/workout-sessions-export?from_date=${fromDate}&to_date=${toDate}&search=${searchValue}&sortField=${sortField}&sortOrder=${sortOrder}&subscription_status=${subscriptionFilter}&user_segment=${userSegment}`,
      auth());
    return (res.data.data || []).map((r) => ({
      ...r,
      latest_timestamp: r.latest_timestamp ? formatUTCDate(r.latest_timestamp) : '',
    }));
  };

  const SortTh = ({ field, label }) => (
    <th className="au-th-sort" onClick={() => handleSort(field)}>
      {label} {sortField === field ? (sortOrder === 'ASC' ? '🔼' : '🔽') : ''}
    </th>
  );
  const drill = (id) => { navigate(`/workout-details/${btoa(id)}`); };
  const hasRange = !!(fromDate || toDate || searchValue || subscriptionFilter);
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };

  const totalRecordsVal = summary.current?.total_records ?? totalRecords;
  const totalUsersVal = summary.current?.total_users ?? totalUsers;

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Activity › <b>Workout sessions</b></span>
            <span className="au-route">/workout-sessions</span>
            <span className="au-spacer" />
            <UserSegmentToggle />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Workout Sessions</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the workout sessions data. Please try again." onRetry={() => loadAll(page, fromDate, toDate, searchValue, sortField, sortOrder, subscriptionFilter)} />
          ) : (
            <>
              {/* Summary before detail */}
              <WorkoutSummaryCards
                totalUsers={totalUsersVal}
                totalRecords={totalRecordsVal}
                totalUserBase={totalUserBase}
                subscribedUserCount={subscribedUserCount}
                deltas={summary.deltas}
              />

              <WorkoutTrendChart data={trend} />

              <WorkoutTopTypesPanel data={topTypes} totalRecords={totalRecordsVal} />

              {/* Filters */}
              <DateRangeBar
                leading={(
                  <>
                    <input className="au-input" type="text" placeholder="Search by name or email" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} aria-label="Search users" />
                    <select className="au-select" name="subscriptionFilter" value={subscriptionFilter} onChange={handleSubscription} aria-label="Subscription filter">
                      <option value="">All Subscriptions</option>
                      <option value="1">Active</option>
                      <option value="2">Cancel</option>
                      <option value="4">Expired</option>
                    </select>
                  </>
                )}
                preset={preset}
                displayFrom={displayFromDate}
                displayTo={displayToDate}
                hasRange={hasRange}
                onPreset={handlePreset}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                onSearch={handleSearch}
                onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="wk-range-note">
                The date range filters workouts by their <b>record date (UTC)</b>; “Latest Timestamp UTC” is each user’s most recent workout in that range.{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all workouts (no date filter).'}
              </p>

              {/* Detail table */}
              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : users.length === 0 ? (
                <EmptyState title="No workouts in this range" hasFilters={hasRange} onClear={clearFilter}
                  message={hasRange ? 'No users match the current filters.' : 'There are no workout records to show for this view yet.'} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Per-user workout records</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="workout-sessions.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table wk-table" data-testid="wk-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Email</th><th>Country</th><th>App Version</th><th>Subscription</th>
                          <SortTh field="record_count" label="Record Count" />
                          <SortTh field="latest_timestamp" label="Latest Timestamp UTC" />
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user, index) => (
                          <tr key={`${user.id}-${index}`}>
                            <td data-label="No">{(page - 1) * LIMIT + index + 1}</td>
                            <td data-label="Name" className="au-ellipsis"><UserDetailLink userId={user.id}>{user.name}</UserDetailLink></td>
                            <td data-label="Email" className="au-ellipsis"><UserDetailLink userId={user.id}>{user.email}</UserDetailLink></td>
                            <td data-label="Country">{user.country}</td>
                            <td data-label="App Version">{user.mobile_app_version}</td>
                            <td data-label="Subscription">{user.subscription_status}</td>
                            <td data-label="Record Count" className="tnum">
                              <Link className="au-link" to={`/workout-details/${btoa(user.id)}`}
                                onClick={(e) => { e.preventDefault(); drill(user.id); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); drill(user.id); } }}>
                                {user.record_count}
                              </Link>
                            </td>
                            <td data-label="Latest Timestamp UTC">{user.latest_timestamp ? formatUTCDate(user.latest_timestamp) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkoutSessionList;
